// client/src/components/SubscriptionModal.tsx - WITH FOUNDING MEMBER TIER
import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../supabase';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface Props {
    onSuccess: () => void;
    onCancel: () => void;
}

type PlanType = 'basic' | 'founding' | 'unlimited';

const PLANS = {
    basic: {
        name: 'Basic',
        price: 25,
        priceId: process.env.REACT_APP_STRIPE_BASIC_PRICE_ID || 'price_basic',
        features: [
            'Search verified medical experts',
            'Message up to 3 experts per month',
            'Post 1 case listing per month',
            'Standard support'
        ],
        limits: { messages_per_month: 3, posts_per_month: 1 }
    },
    founding: {
        name: 'Founding Member',
        price: 49,
        priceId: process.env.REACT_APP_STRIPE_FOUNDING_PRICE_ID || 'price_founding',
        features: [
            'Unlimited expert searches',
            'Unlimited messaging with experts',
            'Unlimited case postings',
            'Priority support',
            '$49/month locked in forever',
            'Founding Member badge',
            'Early access to new features'
        ],
        limits: { messages_per_month: -1, posts_per_month: -1 }
    },
    unlimited: {
        name: 'Unlimited',
        price: 129,
        priceId: process.env.REACT_APP_STRIPE_UNLIMITED_PRICE_ID || 'price_unlimited',
        features: [
            'Unlimited expert searches',
            'Unlimited messaging with experts',
            'Unlimited case postings',
            'Priority support',
            'Advanced filtering options'
        ],
        limits: { messages_per_month: -1, posts_per_month: -1 }
    }
};

export default function SubscriptionModal({ onSuccess, onCancel }: Props) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPlan, setSelectedPlan] = useState<PlanType>('founding');



    const handleSubscribe = async () => {
        setLoading(true);
        setError('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const plan = PLANS[selectedPlan];

            if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
                console.log('Stripe not configured - creating subscription directly');
                await supabase.from('subscriptions').delete().eq('user_id', user.id);
                const { error: insertError } = await supabase.from('subscriptions').insert({
                    user_id: user.id,
                    status: 'active',
                    plan: selectedPlan,
                    amount: plan.price * 100,
                    stripe_subscription_id: 'dev_sub_' + Date.now(),
                    current_period_start: new Date().toISOString(),
                    current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    messages_used: 0, posts_used: 0,
                    messages_limit: plan.limits.messages_per_month,
                    posts_limit: plan.limits.posts_per_month
                });
                if (insertError) throw insertError;
                setTimeout(() => onSuccess(), 500);
                return;
            }

            const stripe = await stripePromise;
            if (!stripe) throw new Error('Stripe not loaded');

            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
            const response = await fetch(`${apiUrl}/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.id,
                    email: user.email,
                    price_id: plan.priceId,
                    plan: selectedPlan,
                    success_url: `${window.location.origin}/attorney-dashboard?success=true`,
                    cancel_url: `${window.location.origin}/attorney-dashboard?cancelled=true`
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Server error: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            if (!data.sessionId) throw new Error('No session ID received');

            const { error: stripeError } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
            if (stripeError) throw stripeError;

        } catch (err: any) {
            console.error('Subscription error:', err);
            setError(err.message || 'An unexpected error occurred');
            setLoading(false);
        }
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto overscroll-contain" onClick={onCancel}>
            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
                <div className="bg-white rounded-xl max-w-5xl w-full p-6 sm:p-8 my-4 sm:my-8 relative" onClick={e => e.stopPropagation()}>
                    <div className="text-center mb-8">
                        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h3>
                        <p className="text-gray-600">Get access to verified medical expert witnesses</p>
                    </div>

                    {/* Plan Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8">
                        {/* Basic Plan */}
                        <div
                            onClick={() => setSelectedPlan('basic')}
                            className={`relative rounded-xl p-5 sm:p-6 cursor-pointer transition-all ${
                                selectedPlan === 'basic'
                                    ? 'border-2 border-blue-600 bg-blue-50 shadow-lg'
                                    : 'border-2 border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            {selectedPlan === 'basic' && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">SELECTED</span>
                                </div>
                            )}
                            <div className="text-center mb-4">
                                <h4 className="text-lg font-bold text-gray-900">{PLANS.basic.name}</h4>
                                <div className="mt-2">
                                    <span className="text-3xl sm:text-4xl font-bold text-gray-900">${PLANS.basic.price}</span>
                                    <span className="text-gray-500">/mo</span>
                                </div>
                            </div>
                            <ul className="space-y-2">
                                {PLANS.basic.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start text-sm">
                                        <svg className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-gray-700">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Founding Member Plan */}
                        <div
                            onClick={() => setSelectedPlan('founding')}
                            className={`relative rounded-xl p-5 sm:p-6 cursor-pointer transition-all ${

                                selectedPlan === 'founding'
                                    ? 'border-2 border-amber-500 bg-amber-50 shadow-lg'
                                    : 'border-2 border-amber-300 hover:border-amber-400 bg-amber-50/30'
                            }`}
                        >
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                {selectedPlan === 'founding' ? 'SELECTED' : 'LIMITED TIME'}
                            </span>
                            </div>
                            <div className="text-center mb-4">
                                <h4 className="text-lg font-bold text-gray-900">{PLANS.founding.name}</h4>
                                <div className="mt-2">
                                    <span className="text-3xl sm:text-4xl font-bold text-amber-700">${PLANS.founding.price}</span>
                                    <span className="text-gray-500">/mo</span>
                                </div>
                                <p className="text-xs text-amber-600 font-medium mt-1">Locked in forever • Save 62%</p>
                            </div>
                            <ul className="space-y-2">
                                {PLANS.founding.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start text-sm">
                                        <svg className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-gray-700">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Unlimited Plan */}
                        <div
                            onClick={() => setSelectedPlan('unlimited')}
                            className={`relative rounded-xl p-5 sm:p-6 cursor-pointer transition-all ${
                                selectedPlan === 'unlimited'
                                    ? 'border-2 border-blue-600 bg-blue-50 shadow-lg'
                                    : 'border-2 border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            {selectedPlan === 'unlimited' && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">SELECTED</span>
                                </div>
                            )}
                            <div className="text-center mb-4">
                                <h4 className="text-lg font-bold text-gray-900">{PLANS.unlimited.name}</h4>
                                <div className="mt-2">
                                    <span className="text-3xl sm:text-4xl font-bold text-gray-900">${PLANS.unlimited.price}</span>
                                    <span className="text-gray-500">/mo</span>
                                </div>
                            </div>
                            <ul className="space-y-2">
                                {PLANS.unlimited.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start text-sm">
                                        <svg className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-gray-700">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                            <p className="font-medium">Error:</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            onClick={handleSubscribe}
                            disabled={loading}
                            className={`flex-1 py-4 px-6 rounded-xl text-white font-semibold text-lg transition ${
                                loading ? 'bg-gray-400 cursor-not-allowed' :
                                    selectedPlan === 'founding' ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' :
                                        'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </span>
                            ) : (
                                `Subscribe — $${PLANS[selectedPlan].price}/month`
                            )}
                        </button>
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="px-8 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 disabled:opacity-50 font-medium"
                        >
                            Cancel
                        </button>
                    </div>

                    {selectedPlan === 'founding' && (
                        <p className="text-xs text-amber-700 text-center mt-4 font-medium">
                            Founding Member pricing is locked at $49/month for the lifetime of your subscription. This is a limited-time offer — once it ends, the regular $129/month rate applies.
                        </p>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-4">
                        By subscribing, you agree to our Terms of Service and Privacy Policy. Cancel anytime.
                    </p>
                </div>
            </div>
        </div>
    );
}
